import { Component, Input, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CommonService } from '../../../services/common.service';
import { SearchConfig, API_STATUS, ApiEnvNames, FETCH_METHODS } from '../../../model/api.model';
import { CommonUtils } from '../../../utils/common.utils';
import { LoadingSkeletonComponent } from "../../../layout/loading-skeleton/loading-skeleton.component";
type TierClass =
  | 'low'
  | 'moderate'
  | 'on-track'
  | 'nearly-full';

@Component({
  selector: 'app-event-registration-reg-report',
  standalone: true,
  imports: [CommonModule, LoadingSkeletonComponent],
  templateUrl: './event-registration-reg-report.component.html',
  styleUrl: './event-registration-reg-report.component.scss'
})
export class EventRegistrationRegComponent implements OnInit {
  @Input() eventId!: number;
  isLoading = false;
  readonly skeletonCards = Array.from({ length: 6 }, (_, idx) => idx);
  private readonly commonService = inject(CommonService);
  private readonly commonUtils = inject(CommonUtils);

  events: any[] = [];
  totalsOverride?: any;

  ngOnInit(): void {
    this.fetchRegistrations();
  }

  fetchRegistrations(): void {
    const fetchParams: SearchConfig = {
      path: 'getEventTotalSeatsPerVenue',
      method: FETCH_METHODS['Get'],
      urlPath: ApiEnvNames['EventDashboard'],
      body: { eventId: this.eventId },
    };
    this.isLoading = true;

    this.commonService.getDataFromDataSource(fetchParams, ApiEnvNames['EventDashboard'])
      .then((response: { status: string; result?: any[] }) => {
        if (response.status === API_STATUS.Success && response.result) {
          this.events = response.result;
        }
      })
      .catch((error: Error) => {
        console.info('Error fetching Event registrations:', error);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private clampPct(val: any): number {
    let n = Number.isFinite(val) ? Number(val) : Number.parseFloat(val);
    n = Number.isNaN(n) ? 0 : n;
    return Math.max(0, Math.min(100, n));
  }

  percent(event: any): number {
    if (event?.percentageFilled != null) {
      return this.clampPct(event.percentageFilled);
    }
    const cap = Number(event?.capacity) || 0;
    const reg = Number(event?.registeredCount) || 0;
    if (!cap) { return 0 };
    return this.clampPct(Math.round((reg / cap) * 100));
  }

  isFull(event: any): boolean {
    return this.percent(event) >= 100;
  }

  spotsRemaining(event: any): number {
    const cap = Number(event?.capacity) || 0;
    const reg = Number(event?.registeredCount) || 0;
    return Math.max(cap - reg, 0);
  }

  tierClassByPercent(percentage: number): TierClass {
    const p = this.clampPct(percentage);
    if (p >= 90) { return 'nearly-full'; }
    if (p >= 70) { return 'on-track'; }
    if (p >= 50) { return 'moderate'; }
    return 'low';
  }
    
  get totalRegistered(): number {
    if (this.totalsOverride?.attended != null) {
      return this.totalsOverride.attended;
    }
    return this.events.reduce((sum, event) => sum + (Number(event.registeredCount) || 0), 0);
  }

  get totalCapacity(): number {
    if (this.totalsOverride?.capacity != null) {
      return this.totalsOverride.capacity;
    }
    return this.events.reduce((sum, event) => sum + (Number(event.capacity) || 0), 0);
  }

  get totalPercent(): number {
    if (!this.totalCapacity) { return 0 };
    return Math.round((this.totalRegistered / this.totalCapacity) * 100);
  }

  get totalLocations(): number {
    return this.events.length;
  }
}

